import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend,
  ResponsiveContainer, ReferenceLine, ReferenceArea, Text,
} from 'recharts';
import {
  SERIES_COLORS, CHART_GRID, CHART_AXIS, AXIS_TICK_STYLE, AXIS_LABEL_STYLE, LINE_PROPS,
  TOOLTIP_CONTENT_STYLE, TOOLTIP_LABEL_STYLE, LEGEND_STYLE,
} from '../simulator/chartTheme';
import ChartZoomHeader from '../simulator/ChartZoomHeader';
import { useChartZoom } from '../simulator/useChartZoom';
import ReferenceLineTopLabel, { REFERENCE_LABEL_TOP_MARGIN, referenceLabelAnchorFor } from '../simulator/ReferenceLineTopLabel';

// MG1 and MG2 speed vs vehicle speed (or ring speed) with the engine speed held constant.
// Rows come from the backend (results.sweep).

const MARK_RED = '#dc2626';
const MARK_DARK = '#334155';
const fmtInt = (v) => Math.round(v).toLocaleString('en-US').replace('-', '−');

// Left-anchored label for a horizontal reference line, just above or below it (Recharts'
// <Text>, so the dev-only visual-edits plugin does not wrap it in an HTML <span>).
const HLineLabel = ({ viewBox, text, color, below }) => {
  if (!viewBox) return null;
  return (
    <Text
      x={viewBox.x + 6} y={below ? viewBox.y + 4 : viewBox.y - 4}
      verticalAnchor={below ? 'start' : 'end'} textAnchor="start"
      fill={color} fontSize={11} fontFamily={AXIS_LABEL_STYLE.fontFamily}
    >
      {text}
    </Text>
  );
};

const SweepChart = ({ sweep }) => {
  const zoom = useChartZoom([0, 'dataMax']);
  const rows = sweep.rows;
  const isSpeed = sweep.xKey === 'speedKmh';
  const xLabel = isSpeed ? 'Vehicle speed (km/h)' : 'Ring speed (rpm)';
  const xUnit = isSpeed ? 'km/h' : 'rpm';
  const lim = sweep.mg1MaxRpm;
  return (
    <div data-testid="ps-sweep-chart">
      <ChartZoomHeader
        caption={`Engine held at ${fmtInt(sweep.engineRpm)} rpm`}
        isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId="zoom-reset-ps-sweep"
      />
      <div className="h-80 md:h-96">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={rows} margin={{ top: REFERENCE_LABEL_TOP_MARGIN, right: 16, left: 4, bottom: 24 }}
            onMouseDown={zoom.onMouseDown} onMouseMove={zoom.onMouseMove} onMouseUp={zoom.onMouseUp}
          >
            <CartesianGrid stroke={CHART_GRID} vertical={false} />
            <XAxis
              dataKey="x" type="number" domain={zoom.xDomain} allowDataOverflow tick={AXIS_TICK_STYLE}
              tickFormatter={(v) => fmtInt(v)}
              label={{ value: xLabel, position: 'insideBottom', offset: -12, style: AXIS_LABEL_STYLE }}
            />
            <YAxis
              tick={AXIS_TICK_STYLE} width={56} tickFormatter={(v) => fmtInt(v)}
              label={{ value: 'Speed (rpm)', angle: -90, position: 'insideLeft', offset: 2, style: AXIS_LABEL_STYLE }}
            />
            <RechartsTooltip
              contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE}
              formatter={(v, name) => [`${fmtInt(v)} rpm`, name]}
              labelFormatter={(v) => `${Number(v).toLocaleString('en-US', { maximumFractionDigits: 1 })} ${xUnit}`}
            />
            <Legend wrapperStyle={{ ...LEGEND_STYLE, paddingTop: 18 }} />
            <ReferenceLine y={0} stroke={CHART_AXIS} />
            <ReferenceLine y={lim} stroke={MARK_RED} strokeDasharray="5 4" ifOverflow="extendDomain"
              label={<HLineLabel text={`MG1 limit +${fmtInt(lim)}`} color={MARK_RED} below />} />
            <ReferenceLine y={-lim} stroke={MARK_RED} strokeDasharray="5 4" ifOverflow="extendDomain"
              label={<HLineLabel text={`MG1 limit −${fmtInt(lim)}`} color={MARK_RED} />} />
            {sweep.mg1ZeroX !== null && (
              <ReferenceLine x={sweep.mg1ZeroX} stroke={SERIES_COLORS[0]} strokeDasharray="3 3"
                label={<ReferenceLineTopLabel text="MG1 = 0" color={SERIES_COLORS[0]} anchor={referenceLabelAnchorFor(sweep.mg1ZeroX, zoom.xDomain, rows, 'x')} />} />
            )}
            {sweep.currentX >= 0 && sweep.currentX <= sweep.xMax && (
              <ReferenceLine x={sweep.currentX} stroke={MARK_DARK} strokeDasharray="4 4"
                label={<ReferenceLineTopLabel text="now" color={MARK_DARK} anchor={referenceLabelAnchorFor(sweep.currentX, zoom.xDomain, rows, 'x')} />} />
            )}
            <Line type="linear" dataKey="mg1Rpm" name="MG1 (sun)" stroke={SERIES_COLORS[0]} {...LINE_PROPS} />
            <Line type="linear" dataKey="mg2Rpm" name="MG2" stroke={SERIES_COLORS[6]} {...LINE_PROPS} />
            <Line type="linear" dataKey="engineRpm" name="Engine (carrier)" stroke={SERIES_COLORS[1]} strokeDasharray="6 4" {...LINE_PROPS} />
            {zoom.isSelecting && (
              <ReferenceArea x1={zoom.refAreaLeft} x2={zoom.refAreaRight} stroke={CHART_AXIS} fill={CHART_AXIS} fillOpacity={0.15} />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SweepChart;
